import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

const TABS = [
  { id: 'resumen', label: 'Resumen', icon: '🏠' },
  { id: 'diario', label: 'Diario', icon: '📖' },
  { id: 'autorizaciones', label: 'Autorizaciones', icon: '✍️' },
  { id: 'salud', label: 'Fichas de salud', icon: '🏥' },
];

const AUTH_TIPOS = {
  salida: { label: 'Salida', color: '#3b82f6' },
  medicacion: { label: 'Medicación', color: '#f59e0b' },
  foto: { label: 'Fotografía', color: '#8b5cf6' },
  otro: { label: 'Otro', color: '#6b7280' },
};

export default function FamilyPortal() {
  const { token } = useAuth();
  const [tab, setTab] = useState('resumen');
  const [loading, setLoading] = useState(true);
  const [resumen, setResumen] = useState(null);
  const [diario, setDiario] = useState([]);
  const [autorizaciones, setAutorizaciones] = useState([]);
  const [fichas, setFichas] = useState([]);
  const [signing, setSigning] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { loadResumen(); }, []);

  useEffect(() => {
    if (tab === 'diario' && diario.length === 0) loadDiario();
    if (tab === 'autorizaciones' && autorizaciones.length === 0) loadAutorizaciones();
    if (tab === 'salud' && fichas.length === 0) loadFichas();
  }, [tab]);

  async function loadResumen() {
    setLoading(true);
    setError('');
    try {
      const r = await api.getPortalResumen(token);
      setResumen(r);
    } catch (e) { setError(e.message || 'Error al cargar el resumen'); }
    setLoading(false);
  }

  async function loadDiario() {
    setError('');
    try {
      const r = await api.getPortalDiario(token);
      setDiario(r.entries || []);
    } catch (e) { setError(e.message || 'Error al cargar el diario'); }
  }

  async function loadAutorizaciones() {
    setError('');
    try {
      const r = await api.getPortalAutorizaciones(token);
      setAutorizaciones(r.autorizaciones || []);
    } catch (e) { setError(e.message || 'Error al cargar autorizaciones'); }
  }

  async function loadFichas() {
    setError('');
    try {
      const r = await api.getPortalFichasSalud(token);
      setFichas(r.fichas || []);
    } catch (e) { setError(e.message || 'Error al cargar fichas de salud'); }
  }

  async function handleFirmar(autId) {
    setSigning(autId);
    try {
      await api.firmarAutorizacion(token, autId);
      await loadAutorizaciones();
    } catch (e) { alert('Error: ' + e.message); }
    setSigning(null);
  }

  if (loading) return (
    <div className="loading-screen" style={{ minHeight: 'auto', padding: '3rem' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  return (
    <div className="main-content page-enter">
      <div style={{ marginBottom: '1.5rem' }}>
        <div className="section-label">Tu espacio</div>
        <h1>Portal familiar</h1>
        <p style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.95rem', marginTop: '.2rem' }}>
          Diario de actividades, autorizaciones y fichas de salud
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '.3rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            className={`btn ${tab === t.id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab(t.id)}
            style={{ fontSize: '.82rem' }}
          >
            {t.icon} {t.label}
            {t.id === 'autorizaciones' && resumen?.pendingAutorizaciones > 0 && (
              <span className="badge badge-amber" style={{ marginLeft: '.4rem', fontSize: '.65rem' }}>
                {resumen.pendingAutorizaciones}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}><span>⚠</span> {error}</div>}

      {/* ── Resumen ── */}
      {tab === 'resumen' && resumen && (
        <div>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <StatCard label="Eventos activos" value={resumen.bookings?.length || 0} color="var(--sage)" />
            <StatCard
              label="Autorizaciones pendientes"
              value={resumen.pendingAutorizaciones || 0}
              color={resumen.pendingAutorizaciones > 0 ? '#f59e0b' : 'var(--sage)'}
            />
            <StatCard label="Novedades (7 dias)" value={resumen.recentDiarioEntries || 0} color="#3b82f6" />
          </div>

          {/* Bookings activas */}
          <div className="section-label" style={{ marginBottom: '.8rem' }}>Tus inscripciones activas</div>
          {(!resumen.bookings || resumen.bookings.length === 0) ? (
            <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--mid)' }}>
              No tienes inscripciones activas
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
              {resumen.bookings.map(b => (
                <div key={b.id} className="card" style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '.95rem' }}>{b.eventName || b.eventId}</div>
                      <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.2rem' }}>
                        {b.members?.map(m => m.name).join(', ')}
                      </div>
                    </div>
                    <span className={`badge ${b.status === 'confirmed' ? 'badge-green' : 'badge-amber'}`}>
                      {b.status === 'confirmed' ? 'Confirmada' : b.status === 'pending_payment' ? 'Pago pendiente' : b.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quick actions */}
          {(resumen.pendingAutorizaciones > 0 || resumen.recentDiarioEntries > 0) && (
            <div style={{ marginTop: '1.5rem' }}>
              <div className="section-label" style={{ marginBottom: '.8rem' }}>Acciones rapidas</div>
              <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap' }}>
                {resumen.pendingAutorizaciones > 0 && (
                  <button className="btn btn-primary" onClick={() => setTab('autorizaciones')}>
                    ✍️ Firmar autorizaciones pendientes ({resumen.pendingAutorizaciones})
                  </button>
                )}
                {resumen.recentDiarioEntries > 0 && (
                  <button className="btn btn-secondary" onClick={() => setTab('diario')}>
                    📖 Ver novedades del diario ({resumen.recentDiarioEntries})
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Diario ── */}
      {tab === 'diario' && (
        <div>
          {diario.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--mid)' }}>
              No hay entradas en el diario todavia
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {diario.map(entry => (
                <div key={entry.id} className="card" style={{ padding: '1.2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.6rem' }}>
                    <div>
                      <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--white)', margin: 0 }}>
                        {entry.titulo}
                      </h3>
                      <div style={{ fontSize: '.75rem', color: 'var(--sage)', marginTop: '.2rem' }}>
                        {entry.eventoNombre}
                      </div>
                    </div>
                    <div style={{ fontSize: '.75rem', color: 'var(--mid)', whiteSpace: 'nowrap' }}>
                      {entry.fecha ? new Date(entry.fecha + 'T00:00:00').toLocaleDateString('es-ES', {
                        weekday: 'long', day: 'numeric', month: 'long'
                      }) : ''}
                    </div>
                  </div>
                  <p style={{ fontSize: '.9rem', color: 'var(--white)', lineHeight: 1.65, whiteSpace: 'pre-wrap', margin: 0 }}>
                    {entry.contenido}
                  </p>
                  {entry.fotos?.length > 0 && (
                    <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginTop: '.8rem' }}>
                      {entry.fotos.map((url, i) => (
                        <img key={i} src={url} alt="" style={{
                          width: 120, height: 90, objectFit: 'cover', borderRadius: 'var(--radius)',
                          border: '1px solid rgba(122,171,138,.2)',
                        }} />
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: '.7rem', color: 'var(--mid)', marginTop: '.6rem' }}>
                    Publicado por {entry.creadoPorNombre}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Autorizaciones ── */}
      {tab === 'autorizaciones' && (
        <div>
          {autorizaciones.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--mid)' }}>
              No hay autorizaciones pendientes
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
              {/* Pendientes primero */}
              {autorizaciones.filter(a => !a.firmada).length > 0 && (
                <div className="section-label" style={{ color: '#f59e0b' }}>Pendientes de firma</div>
              )}
              {autorizaciones.filter(a => !a.firmada).map(aut => {
                const tipo = AUTH_TIPOS[aut.tipo] || AUTH_TIPOS.otro;
                return (
                  <div key={aut.id} className="card" style={{
                    padding: '1rem', borderLeft: '4px solid #f59e0b',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '.8rem' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.3rem' }}>
                          <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--white)' }}>{aut.titulo}</span>
                          <span className="badge" style={{
                            background: tipo.color + '22', color: tipo.color,
                            border: `1px solid ${tipo.color}44`, fontSize: '.65rem',
                          }}>{tipo.label}</span>
                          {aut.obligatoria && (
                            <span className="badge badge-danger" style={{ fontSize: '.65rem' }}>Obligatoria</span>
                          )}
                        </div>
                        <div style={{ fontSize: '.75rem', color: 'var(--sage)', marginBottom: '.4rem' }}>{aut.eventoNombre}</div>
                        {aut.descripcion && (
                          <p style={{ fontSize: '.85rem', color: 'var(--mid)', lineHeight: 1.5, margin: 0 }}>
                            {aut.descripcion}
                          </p>
                        )}
                      </div>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleFirmar(aut.id)}
                        disabled={signing === aut.id}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {signing === aut.id ? <><span className="spinner" /> Firmando...</> : '✍️ Firmar'}
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Firmadas */}
              {autorizaciones.filter(a => a.firmada).length > 0 && (
                <>
                  <div className="section-label" style={{ color: 'var(--sage)', marginTop: '.5rem' }}>Firmadas</div>
                  {autorizaciones.filter(a => a.firmada).map(aut => {
                    const tipo = AUTH_TIPOS[aut.tipo] || AUTH_TIPOS.otro;
                    const fechaFirma = aut.firma?.fechaFirma;
                    let fechaStr = '';
                    if (fechaFirma) {
                      const d = fechaFirma._seconds ? new Date(fechaFirma._seconds * 1000) : new Date(fechaFirma);
                      fechaStr = d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
                    }
                    return (
                      <div key={aut.id} className="card" style={{
                        padding: '1rem', borderLeft: '4px solid var(--sage)', opacity: .8,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                              <span style={{ fontWeight: 600, fontSize: '.9rem', color: 'var(--white)' }}>{aut.titulo}</span>
                              <span className="badge" style={{
                                background: tipo.color + '22', color: tipo.color,
                                border: `1px solid ${tipo.color}44`, fontSize: '.65rem',
                              }}>{tipo.label}</span>
                            </div>
                            <div style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: '.2rem' }}>
                              {aut.eventoNombre} {fechaStr && `· Firmada el ${fechaStr}`}
                            </div>
                          </div>
                          <span className="badge badge-green">Firmada</span>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Fichas de salud ── */}
      {tab === 'salud' && (
        <div>
          {fichas.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--mid)' }}>
              No hay fichas de salud disponibles
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
              {fichas.map((f, i) => (
                <div key={`${f.bookingId}-${f.memberIndex}`} className="card" style={{ padding: '1.1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.8rem' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--white)' }}>{f.name}</h3>
                        {f.edad != null && (
                          <span style={{ fontSize: '.75rem', color: 'var(--mid)' }}>({f.edad} a.)</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginTop: '.2rem' }}>
                        <span style={{ fontSize: '.75rem', color: 'var(--sage)' }}>{f.eventName}</span>
                        {f.source === 'persona' && (
                          <span className="badge" style={{
                            background: 'rgba(122,171,138,.15)', color: 'var(--sage)',
                            border: '1px solid rgba(122,171,138,.3)', fontSize: '.6rem',
                          }}>Datos actualizados</span>
                        )}
                      </div>
                    </div>
                    {f.grupoSanguineo && (
                      <span className="badge" style={{
                        background: 'rgba(239,68,68,.15)', color: '#ef4444',
                        border: '1px solid rgba(239,68,68,.3)', fontWeight: 700,
                      }}>
                        {f.grupoSanguineo}
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem .8rem' }}>
                    <FieldDisplay label="Alergias" value={f.alergias} warn={!!f.alergias} />
                    <FieldDisplay label="Enfermedades" value={f.enfermedades} />
                    <FieldDisplay label="Medicacion actual" value={f.medicacionActual} />
                    <FieldDisplay label="Tarjeta sanitaria" value={f.tarjetaSanitaria} />
                  </div>

                  <div style={{ marginTop: '.8rem', padding: '.7rem', background: 'rgba(255,255,255,.03)', borderRadius: 'var(--radius)', border: '1px solid rgba(122,171,138,.12)' }}>
                    <div style={{ fontSize: '.7rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--mid)', marginBottom: '.4rem' }}>
                      Contacto de emergencia
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '.4rem' }}>
                      <FieldDisplay label="Nombre" value={f.contactoEmergenciaNombre} />
                      <FieldDisplay label="Relacion" value={f.contactoEmergenciaRelacion} />
                      <FieldDisplay label="Telefono" value={f.contactoEmergenciaTelefono ? `${f.contactoEmergenciaExt || '+34'} ${f.contactoEmergenciaTelefono}` : null} />
                    </div>
                  </div>

                  <div style={{ marginTop: '.5rem', display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                    <span style={{ fontSize: '.78rem', color: f.autorizaMedicacion ? 'var(--sage)' : '#ef4444' }}>
                      {f.autorizaMedicacion ? '✓ Autoriza administracion de medicacion' : '✗ No autoriza administracion de medicacion'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,.04)', border: '1px solid rgba(122,171,138,.15)',
      borderRadius: 'var(--radius)', padding: '1rem', textAlign: 'center',
    }}>
      <div style={{ fontSize: '1.8rem', fontWeight: 800, color, fontFamily: "'Fraunces',serif" }}>{value}</div>
      <div style={{ fontSize: '.72rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</div>
    </div>
  );
}

function FieldDisplay({ label, value, warn }) {
  return (
    <div style={{ minWidth: 0, overflow: 'hidden' }}>
      <div style={{ fontSize: '.65rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--mid)', marginBottom: '.1rem' }}>
        {label}
      </div>
      <div style={{
        fontSize: '.85rem',
        color: value ? (warn ? '#f59e0b' : 'var(--white)') : 'var(--mid)',
        fontStyle: value ? 'normal' : 'italic',
        wordBreak: 'break-word',
      }}>
        {value || 'No indicado'}
      </div>
    </div>
  );
}
